import React, { useState, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api } from "@/services/api";
import toast from "react-hot-toast";
import { X, Calendar, CreditCard, FileText } from "lucide-react";

const emptyPayment = {
  organization_id: "",
  customer_id: "",
  invoice_id: "",
  amount: "",
  payment_date: new Date().toISOString().split('T')[0],
  payment_method: "",
  notes: "",
  is_general_credit: false
};

export default function PaymentForm({ onClose, onSuccess, invoiceId: defaultInvoiceId, customerId: defaultCustomerId }) {
  const [form, setForm] = useState({ ...emptyPayment, invoice_id: defaultInvoiceId || "", customer_id: defaultCustomerId || "" });
  const [isGeneralCredit, setIsGeneralCredit] = useState(!defaultInvoiceId);
  const isSpecificInvoicePayment = !!defaultInvoiceId;
  const [invoiceSearch, setInvoiceSearch] = useState("");
  const [customerSearch, setCustomerSearch] = useState("");
  const [showInvoiceDropdown, setShowInvoiceDropdown] = useState(false);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);

  // Fetch invoice details when a specific invoice is provided
  const { data: invoiceDetails } = useQuery({
    queryKey: ["invoice", defaultInvoiceId],
    queryFn: async () => (await api.get(`/invoice/${defaultInvoiceId}`)).data,
    enabled: !!defaultInvoiceId,
  });

  // Fetch invoice status to get outstanding amount
  const { data: invoiceStatus } = useQuery({
    queryKey: ["invoice-status", defaultInvoiceId],
    queryFn: async () => (await api.get(`/invoices/${defaultInvoiceId}/status`)).data,
    enabled: !!defaultInvoiceId,
  });

  // Pre-fill organization and customer when invoice details are loaded
  useEffect(() => {
    if (invoiceDetails && isSpecificInvoicePayment) {
      setForm(prev => ({
        ...prev,
        organization_id: invoiceDetails.organization_id,
        customer_id: invoiceDetails.customer_id,
        invoice_id: defaultInvoiceId,
      }));
    }
  }, [invoiceDetails, defaultInvoiceId, isSpecificInvoicePayment]);

  // Pre-fill organization when customerId is provided (for general credit)
  useEffect(() => {
    if (defaultCustomerId && !isSpecificInvoicePayment) {
      setForm(prev => ({
        ...prev,
        customer_id: defaultCustomerId,
      }));
    }
  }, [defaultCustomerId, isSpecificInvoicePayment]);

  const { data: orgs = [] } = useQuery({ 
    queryKey: ["orgs"], 
    queryFn: async () => (await api.get("/organizations")).data,
    enabled: !isSpecificInvoicePayment
  });
  
  const { data: customers = [] } = useQuery({
    queryKey: ["customers", form.organization_id],
    queryFn: async () => (await api.get("/customers", { params: { organization_id: form.organization_id } })).data,
    enabled: !!form.organization_id && !isSpecificInvoicePayment,
  });

  const { data: invoices = [] } = useQuery({
    queryKey: ["invoices", form.organization_id, form.customer_id, isGeneralCredit],
    queryFn: async () => (await api.get("/invoices", { 
      params: { 
        organization_id: form.organization_id, 
        customer_id: !isGeneralCredit ? form.customer_id : undefined,
        status: "pending,overdue"
      } 
    })).data,
    enabled: !!form.organization_id && !isGeneralCredit && !isSpecificInvoicePayment,
  });

  // Update search values when invoice/customer changes
  useEffect(() => {
    if (form.invoice_id && Array.isArray(invoices)) {
      const selectedInvoice = invoices.find(i => i.id === form.invoice_id);
      if (selectedInvoice) {
        setInvoiceSearch(selectedInvoice.invoice_number);
      }
    }
  }, [form.invoice_id, invoices]);

  useEffect(() => {
    if (form.customer_id && Array.isArray(customers)) {
      const selectedCustomer = customers.find(c => c.id === form.customer_id);
      if (selectedCustomer) {
        setCustomerSearch(selectedCustomer.name);
      }
    }
  }, [form.customer_id, customers]);

  const filteredInvoices = Array.isArray(invoices) 
    ? invoices.filter(i => i.invoice_number.toLowerCase().includes(invoiceSearch.toLowerCase()))
    : [];

  const filteredCustomers = Array.isArray(customers)
    ? customers.filter(c => c.name.toLowerCase().includes(customerSearch.toLowerCase()))
    : [];

  const createPayment = useMutation({
    mutationFn: async () => {
      const payload = {
        organization_id: form.organization_id,
        customer_id: isGeneralCredit ? form.customer_id : undefined,
        invoice_id: isGeneralCredit ? undefined : form.invoice_id,
        amount: parseFloat(form.amount),
        payment_date: form.payment_date,
        payment_method: form.payment_method,
        notes: form.notes,
      };
      return (await api.post("/payments", payload)).data;
    },
    onSuccess: () => {
      toast.success("Payment recorded successfully");
      setForm(emptyPayment);
      onSuccess();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || e.message || "Failed to record payment"),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.organization_id || !form.amount || !form.payment_date) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (!isGeneralCredit && !form.invoice_id) {
      toast.error("Please select an invoice");
      return;
    }
    if (isGeneralCredit && !form.customer_id) {
      toast.error("Please select a customer");
      return;
    }
    createPayment.mutate();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-[#121214] rounded-sm w-full max-w-lg max-h-[90vh] overflow-auto">
        <div className="p-6 flex items-center justify-between border-b border-slate-200 dark:border-slate-800">
          <h2 className="text-2xl font-extrabold tracking-tighter">Record Payment</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {isSpecificInvoicePayment && invoiceDetails && (
            <div className="p-4 bg-slate-50 dark:bg-slate-900 rounded-sm mb-4">
              <div className="font-semibold mb-2">Payment for Invoice</div>
              <div className="text-sm text-slate-600 dark:text-slate-400">
                {invoiceDetails.invoice_number} - ₹{Number(invoiceDetails.total).toLocaleString("en-IN")}
              </div>
              <div className="flex items-center justify-between mt-2">
                <div className="text-xs text-slate-500">
                  Status: {invoiceStatus?.calculated_status || invoiceDetails.status}
                </div>
                {invoiceStatus && (
                  <div className={`text-sm font-semibold ${invoiceStatus.current_balance > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                    {invoiceStatus.current_balance > 0 
                      ? `Outstanding: ₹${Number(invoiceStatus.current_balance).toLocaleString("en-IN")}`
                      : invoiceStatus.current_balance < 0 
                      ? `Overpaid: ₹${Math.abs(invoiceStatus.current_balance).toLocaleString("en-IN")}`
                      : 'Fully Paid'
                    }
                  </div>
                )}
              </div>
              {invoiceStatus && invoiceStatus.current_balance > 0 && (
                <div className="mt-2 pt-2 border-t border-slate-200 dark:border-slate-700 text-xs text-slate-500">
                  Enter amount to record payment
                </div>
              )}
            </div>
          )}

          <label>
            <span className="uppercase text-[10px] tracking-[0.2em] text-slate-500">Organization *</span>
            <select
              value={form.organization_id}
              onChange={(e) => setForm({ ...form, organization_id: e.target.value, customer_id: "", invoice_id: "" })}
              className="mt-1 w-full border border-slate-300 dark:border-slate-700 bg-transparent px-3 py-2 rounded-sm"
              required
              disabled={isSpecificInvoicePayment}
            >
              <option value="">Select organization…</option>
              {orgs.map((o) => (<option key={o.id} value={o.id}>{o.organization_name}</option>))}
            </select>
            {isSpecificInvoicePayment && (
              <div className="text-xs text-slate-500 mt-1">Organization is pre-selected for this invoice</div>
            )}
          </label>

          {!isSpecificInvoicePayment && (
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={!isGeneralCredit}
                  onChange={() => setIsGeneralCredit(false)}
                  className="w-4 h-4"
                />
                <span className="text-sm">Invoice Payment</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={isGeneralCredit}
                  onChange={() => setIsGeneralCredit(true)}
                  className="w-4 h-4"
                />
                <span className="text-sm">General Credit</span>
              </label>
            </div>
          )}

          {!isGeneralCredit && (
            <label>
              <span className="uppercase text-[10px] tracking-[0.2em] text-slate-500">Invoice *</span>
              <div className="relative">
                <input
                  type="text"
                  value={invoiceSearch}
                  onChange={(e) => {
                    setInvoiceSearch(e.target.value);
                    setShowInvoiceDropdown(true);
                  }}
                  onFocus={() => setShowInvoiceDropdown(true)}
                  onBlur={() => setTimeout(() => setShowInvoiceDropdown(false), 200)}
                  className="mt-1 w-full border border-slate-300 dark:border-slate-700 bg-transparent px-3 py-2 rounded-sm"
                  placeholder="Search invoice number..."
                  required
                  disabled={isSpecificInvoicePayment}
                />
                {showInvoiceDropdown && invoiceSearch && !isSpecificInvoicePayment && (
                  <div className="absolute z-10 w-full mt-1 bg-white dark:bg-[#121214] border border-slate-300 dark:border-slate-700 rounded-sm max-h-48 overflow-auto shadow-lg">
                    {filteredInvoices.length === 0 ? (
                      <div className="p-2 text-xs text-slate-500">No invoices found</div>
                    ) : (
                      filteredInvoices.map((i) => (
                        <div
                          key={i.id}
                          onClick={() => {
                            setForm({ 
                              ...form, 
                              invoice_id: i.id,
                              customer_id: i.customer_id || form.customer_id
                            });
                            setInvoiceSearch(i.invoice_number);
                            setShowInvoiceDropdown(false);
                          }}
                          className="p-2 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 border-b border-slate-200 dark:border-slate-700 last:border-0"
                        >
                          {i.invoice_number} - ₹{Number(i.total).toLocaleString("en-IN")} ({i.calculated_status})
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
              {isSpecificInvoicePayment && (
                <div className="text-xs text-slate-500 mt-1">Invoice is pre-selected for this payment</div>
              )}
            </label>
          )}

          {isGeneralCredit && (
            <label>
              <span className="uppercase text-[10px] tracking-[0.2em] text-slate-500">Customer *</span>
              <div className="relative">
                <input
                  type="text"
                  value={customerSearch}
                  onChange={(e) => {
                    setCustomerSearch(e.target.value);
                    setShowCustomerDropdown(true);
                  }}
                  onFocus={() => setShowCustomerDropdown(true)}
                  onBlur={() => setTimeout(() => setShowCustomerDropdown(false), 200)}
                  className="mt-1 w-full border border-slate-300 dark:border-slate-700 bg-transparent px-3 py-2 rounded-sm"
                  placeholder="Search customer..."
                  required
                />
                {showCustomerDropdown && customerSearch && (
                  <div className="absolute z-10 w-full mt-1 bg-white dark:bg-[#121214] border border-slate-300 dark:border-slate-700 rounded-sm max-h-48 overflow-auto shadow-lg">
                    {filteredCustomers.length === 0 ? (
                      <div className="p-2 text-xs text-slate-500">No customers found</div>
                    ) : (
                      filteredCustomers.map((c) => (
                        <div
                          key={c.id}
                          onClick={() => {
                            setForm({ ...form, customer_id: c.id });
                            setCustomerSearch(c.name);
                            setShowCustomerDropdown(false);
                          }}
                          className="p-2 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 border-b border-slate-200 dark:border-slate-700 last:border-0"
                        >
                          {c.name}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </label>
          )}

          <label>
            <span className="uppercase text-[10px] tracking-[0.2em] text-slate-500">Amount *</span>
            <div className="mt-1 relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-semibold">₹</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                onWheel={(e) => e.target.blur()}
                className="w-full border border-slate-300 dark:border-slate-700 bg-transparent pl-8 pr-3 py-2 rounded-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                placeholder="0.00"
                required
              />
            </div>
          </label>

          <label>
            <span className="uppercase text-[10px] tracking-[0.2em] text-slate-500">Payment Date *</span>
            <div className="mt-1 relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="date"
                value={form.payment_date}
                onChange={(e) => setForm({ ...form, payment_date: e.target.value })}
                className="w-full border border-slate-300 dark:border-slate-700 bg-transparent pl-10 pr-3 py-2 rounded-sm"
                required
              />
            </div>
          </label>

          <label>
            <span className="uppercase text-[10px] tracking-[0.2em] text-slate-500">Payment Method</span>
            <div className="mt-1 relative">
              <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <select
                value={form.payment_method}
                onChange={(e) => setForm({ ...form, payment_method: e.target.value })}
                className="w-full border border-slate-300 dark:border-slate-700 bg-transparent pl-10 pr-3 py-2 rounded-sm"
              >
                <option value="">Select method…</option>
                <option value="UPI">UPI</option>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="Cash">Cash</option>
                <option value="Cheque">Cheque</option>
                <option value="Card">Card</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </label>

          <label>
            <span className="uppercase text-[10px] tracking-[0.2em] text-slate-500">Notes</span>
            <textarea
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="mt-1 w-full border border-slate-300 dark:border-slate-700 bg-transparent px-3 py-2 rounded-sm"
              placeholder="Any additional notes..."
            />
          </label>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-800">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 border border-slate-300 dark:border-slate-700 rounded-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createPayment.isPending}
              className="px-5 py-2.5 bg-[#002FA7] text-white rounded-sm hover:bg-[#001F73] disabled:opacity-50"
            >
              {createPayment.isPending ? "Recording…" : "Record Payment"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
